import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { UserStatusComponent } from '@app/components/user/user-status/user-status.component';
import { ProffeoDatePipe } from '@app/shared/pipes/proffeo-date.pipe';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { UserStatus } from '@app/shared/types/enums/user-status.enum';
import { User } from '@app/shared/types/models/user/user.model';
import { ProffeoUiTableCellTemplateContext, ProffeoUiTableColumn, UiTableComponent } from '@app/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-users-list',
  imports: [MatIconModule, ProffeoDatePipe, TranslatePipe, UserStatusComponent, MatMenuModule, UiTableComponent],
  templateUrl: './users-list.component.html'
})
export class UsersListComponent {
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly emailCellTemplate = viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<User>>>('emailCell');
  private readonly phoneCellTemplate = viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<User>>>('phoneCell');
  private readonly statusCellTemplate = viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<User>>>('statusCell');
  private readonly createdAtCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<User>>>('createdAtCell');
  private readonly profileCompletionCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<User>>>('profileCompletionCell');
  private readonly actionsCellTemplate = viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<User>>>('actionsCell');

  protected readonly UserStatus = UserStatus;
  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected readonly columns = computed((): ProffeoUiTableColumn<User>[] => [
    {
      columnId: 'email',
      columnName: this.translateService.instant('users-list-desktop-view-email'),
      dataType: 'string',
      accessor: user => user.email ?? '',
      sortable: false,
      hideable: false,
      widthPx: 230,
      minWidthPx: 150,
      template: this.emailCellTemplate()
    },
    {
      columnId: 'fullName',
      columnName: this.translateService.instant('table-headers.full-name'),
      dataType: 'string',
      accessor: user => user.fullName,
      sortable: false,
      hideable: false,
      widthPx: 170,
      minWidthPx: 130
    },
    {
      columnId: 'phone',
      columnName: this.translateService.instant('users-list-desktop-view-profile-phone'),
      dataType: 'string',
      accessor: user => user.phoneNumbers ?? '',
      sortable: false,
      hideable: false,
      widthPx: 120,
      minWidthPx: 100,
      template: this.phoneCellTemplate()
    },
    {
      columnId: 'status',
      columnName: this.translateService.instant('users-list-desktop-view-status'),
      dataType: 'string',
      accessor: user => user.status,
      sortable: false,
      hideable: false,
      widthPx: 120,
      minWidthPx: 100,
      align: 'center',
      template: this.statusCellTemplate()
    },
    {
      columnId: 'createdAt',
      columnName: this.translateService.instant('table-headers.created-at'),
      dataType: 'date',
      accessor: user => user.createdAt,
      sortable: false,
      hideable: false,
      widthPx: 140,
      minWidthPx: 110,
      align: 'center',
      template: this.createdAtCellTemplate()
    },
    {
      columnId: 'profileCompletion',
      columnName: this.translateService.instant('users-list-desktop-view-profile-completion'),
      dataType: 'number',
      accessor: user => user.profileCompletion,
      sortable: false,
      hideable: false,
      widthPx: 130,
      minWidthPx: 110,
      align: 'center',
      template: this.profileCompletionCellTemplate()
    },
    {
      columnId: 'actions',
      columnName: '',
      dataType: 'object',
      accessor: user => user,
      sortFunction: () => 0,
      sortable: false,
      hideable: false,
      expandable: false,
      widthPx: 56,
      align: 'center',
      template: this.actionsCellTemplate()
    }
  ]);

  public users: InputSignal<User[]> = input.required<User[]>();
  public deleteUser: OutputEmitterRef<User> = output<User>();
  public lockUser: OutputEmitterRef<User> = output<User>();
  public unlockUser: OutputEmitterRef<User> = output<User>();
  public resendActivationLink: OutputEmitterRef<User> = output<User>();

  protected resolveRowId(user: User): string {
    return user.id;
  }

  protected onLockUser(user: User): void {
    this.lockUser.emit(user);
  }

  protected onUnlockUser(user: User): void {
    this.unlockUser.emit(user);
  }

  protected onResendActivationLink(user: User): void {
    this.resendActivationLink.emit(user);
  }

  protected stopRowActivation(event: Event): void {
    event.stopPropagation();
  }

  protected openEditUserDialog(user: User): void {
    this.dialogService.openEditUserDialog(user);
  }

  protected openUserDeleteConfirmationDialog(user: User): void {
    this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('modals.delete-user-modal-title'),
        desc: this.translateService.instant('modals.delete-user-modal-desc', { value: user.fullName })
      })
      .afterClosed()
      .subscribe(decision => {
        if (decision) {
          this.deleteUser.emit(user);
        }
      });
  }
}
